import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy, MessageSquare } from 'lucide-react';
import { TICKET_CATEGORIES, TICKET_PRIORITY, type DeskAttachmentInput, type TicketCategory, type TicketDetail } from '@his/shared';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { AttachmentPicker } from './AttachmentPicker';
import { TENANT_DESK, type DeskEndpoints } from './desk-api';

const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  general: 'Something else',
  licence_change: 'A change to what we are licensed for',
  payment_proof: 'Proof of payment (attach it below)',
};

export function ticketCategoryFrom(value: string | null): TicketCategory {
  return (TICKET_CATEGORIES as readonly string[]).includes(value ?? '') ? (value as TicketCategory) : 'general';
}

export function NewTicketForm({
  endpoints,
  listPath,
  tenantChoices,
  initialCategory,
}: {
  endpoints: DeskEndpoints;
  listPath: string;
  tenantChoices?: { id: string; name: string }[];
  initialCategory: TicketCategory;
}) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), listPath);
  const initialTenantId = params.get('tenantId') ?? tenantChoices?.[0]?.id ?? '';
  const [tenantId, setTenantId] = useState(initialTenantId);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [category, setCategory] = useState<TicketCategory>(initialCategory);
  const [files, setFiles] = useState<DeskAttachmentInput[]>([]);
  const [error, setError] = useState<string | null>(null);

  const needsTenant = Boolean(tenantChoices);
  const valid = subject.trim().length >= 5 && body.trim().length >= 10 && (!needsTenant || Boolean(tenantId));

  const create = useMutation({
    mutationFn: () =>
      post<TicketDetail>(endpoints.tickets, {
        ...(needsTenant ? { tenantId } : {}),
        subject: subject.trim(),
        body: body.trim(),
        priority,
        category,
        attachments: files,
      }),
    onSuccess: (ticket) => {
      toast.success(`Ticket ${ticket.number} opened`);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['client-profile'] });
      navigate(`${listPath}?ticket=${ticket.id}`, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty =
    !create.isSuccess && (subject !== '' || body !== '' || priority !== 'normal' || category !== initialCategory || files.length > 0 || tenantId !== initialTenantId);

  return (
    <FormScreen
      title="Open a support ticket"
      description="Describe what happened and attach a screenshot if you have one."
      icon={LifeBuoy}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={LifeBuoy}>
        {tenantChoices && (
          <Field label="Hospital" required className="sm:col-span-2">
            <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              <option value="">Select a hospital…</option>
              {tenantChoices.map((choice) => (
                <option key={choice.id} value={choice.id}>
                  {choice.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Subject" required hint="One line — what is not working" className="sm:col-span-2">
          <Input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field label="About" className="sm:col-span-2">
          <ChoiceButtons
            layout="cards"
            columns={3}
            value={category}
            onChange={setCategory}
            options={TICKET_CATEGORIES.map((value) => ({ value, label: TICKET_CATEGORY_LABELS[value] }))}
          />
        </Field>
        <Field label="Priority" required>
          <ChoiceButtons
            layout="chips"
            value={priority}
            onChange={setPriority}
            required
            options={TICKET_PRIORITY.map((value) => ({ value, label: humanize(value, 'priority') }))}
          />
        </Field>
      </FormSection>

      <FormSection title={tr('ipd', 'what_happened')} icon={MessageSquare} columns={1}>
        <Field label="What happened" required hint="What you were doing, and what you expected instead">
          <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <AttachmentPicker
          endpoints={endpoints}
          extra={needsTenant && tenantId ? { tenantId } : undefined}
          value={files}
          onChange={setFiles}
          disabled={needsTenant && !tenantId}
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Open ticket" loading={create.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}

export function TenantNewTicketPage() {
  const [params] = useSearchParams();
  return <NewTicketForm endpoints={TENANT_DESK} listPath="/support" initialCategory={ticketCategoryFrom(params.get('category'))} />;
}
